import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "Is depression at 70 just a normal part of aging?",
    answer:
      "No. Depression is not a normal part of aging. Most people in their seventies are not depressed. The low mood, the loss of interest, the poor sleep, the fatigue, the aches that don't resolve — those are symptoms of an illness, not signs that your life is winding down. There is a reason you've felt this way, and the reason has treatments.",
  },
  {
    question: "Could my memory problems be depression instead of dementia?",
    answer:
      "Very possibly. Depression is one of the most common causes of memory and concentration problems in older adults, and when it's treated, the thinking often improves. It does not mean dementia. A proper evaluation can distinguish between the two, and treating the depression may resolve the cognitive symptoms entirely.",
  },
  {
    question: "Is it too late to treat depression at 70?",
    answer:
      "No. Therapy works at 70; older adults respond to it well, and it's often underused. Antidepressants can help, chosen carefully. And SAINT, a non-drug, FDA-cleared brain stimulation treatment, takes five days with no anesthesia and no interaction with your other medications. In clinical trials, 79 percent of people who had failed medication reached remission. It's not too late.",
  },
  {
    question: "What if my parent is depressed and I'm reading this for them?",
    answer:
      "Don't let it be filed under aging. Help them get to the appointment. Help them give the treatment a real chance. And if medication hasn't worked or hasn't been tolerable, ask about non-drug options. Treating their depression is not just about mood — it affects their heart, their memory, their mobility, and how many good years they have left. Family members are welcome at every step at Neuro Wellness TMS Centers of America.",
  },
];

const related: RelatedLink[] = [
  {
    title: 'Diagnosed With Depression at 60: This Is Not "Just Getting Older"',
    href: "/diagnosed-with-depression-at-60",
    description:
      "Why depression at 60 is the most misdiagnosed and undertreated it will ever be.",
  },
  {
    title:
      "Diagnosed With Depression at 80: It Is Not Too Late, and It Is Not Just Age",
    href: "/diagnosed-with-depression-at-80",
    description:
      "Why treating depression at 80 is not a luxury — it's medicine that affects every other health condition.",
  },
  {
    title:
      "Depression in the Second Half of Life: What Happens Next Is Up to You",
    href: "/depression-in-the-second-half-of-life",
    description:
      "Why the second half of life is not a reason to settle — and what treatment looks like now.",
  },
  {
    title: "How SAINT® Compares to Other Depression Treatment Options",
    href: "/how-saint-compares-to-other-depression-treatment-options",
    description:
      "A side-by-side comparison of SAINT® against medication, standard TMS, esketamine, and ECT.",
  },
];

export default function DiagnosedDepressionAt70() {
  return (
    <DepressionContentPage
      seo={{
        title:
          "Diagnosed With Depression at 70? You Have Every Right to Feel Better",
        description:
          "Depression at 70 is not a normal part of aging. What the diagnosis means, why it shows up, what treatment looks like, and a non-drug option that takes five days — with no anesthesia and no drug interactions.",
        canonical: "/diagnosed-with-depression-at-70",
        keywords:
          "diagnosed with depression at 70, major depressive disorder seventies, treatment-resistant depression older adult, SAINT TMS, depression not aging, memory problems depression, non-drug depression treatment, fMRI-guided TMS, elderly depression treatment",
      }}
      badge="For Adults"
      heroTitle="Diagnosed With Depression at 70: You Have Every Right to Feel Better"
      heroSubtitle="There's a particular quietness to depression at 70. It doesn't announce itself. Family assumes it's age. Doctors focused on blood pressure and joints may not ask. If a doctor has told you that you have major depressive disorder, please hear this clearly: depression is not a normal part of aging, and treating it is not a luxury. It's medicine."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                There's a particular quietness to depression at 70. It doesn't
                announce itself. It looks like a person who has slowed down, who
                doesn't call as often, who says they're fine and doesn't want to
                be a bother. Family assumes it's age. Doctors, focused on blood
                pressure and joints, may not ask. And the person themselves has
                often decided that this is simply what the later years are like.
              </p>
              <p>
                It isn't. If a doctor has told you that you have major
                depressive disorder, please hear this clearly: depression is not
                a normal part of aging, it is not something you have to accept,
                and treating it at 70 is not a luxury. It's medicine.
              </p>
            </div>
          ),
        },
        {
          heading: "What the diagnosis means",
          body: (
            <div className="space-y-5">
              <p>
                It means the low mood, the loss of interest, the poor sleep, the
                fatigue, the trouble concentrating, and the aches that don't
                resolve are symptoms of an illness, not signs that your life is
                winding down. It means there is a reason you've felt this way,
                and that the reason has treatments.
              </p>
              <p>
                It does not mean dementia. Depression in older adults often
                causes memory and concentration problems that look like
                cognitive decline, and they typically improve when the
                depression is treated. It does not mean you're a burden. And it
                does not mean it's too late.
              </p>
            </div>
          ),
        },
        {
          heading: "Why depression shows up at 70",
          body: (
            <div className="space-y-5">
              <p>
                Loss accumulates. A spouse, siblings, lifelong friends. Physical
                health changes: chronic pain, heart disease, vision and hearing
                loss, the medications for all of it, some of which affect mood.
                Mobility narrows the world. Retirement is long behind you and
                the structure it provided is gone. Adult children are far away
                or occupied. Loneliness is one of the strongest risk factors for
                depression at any age, and it's most common at this one.
              </p>
            </div>
          ),
        },
        {
          heading: "What treatment looks like",
          body: (
            <div className="space-y-5">
              <p>
                Therapy works at 70; older adults respond to it well, and it's
                often underused. Antidepressants can help, chosen carefully
                because of interactions with other medications and greater
                sensitivity to side effects. Your doctor should rule out medical
                causes that mimic depression: thyroid, B12, sleep apnea,
                medication effects. Social connection and physical activity, at
                whatever level you can manage, matter enormously.
              </p>
              <p>For many people, this is enough.</p>
            </div>
          ),
        },
        {
          heading: "When medication isn't enough, or isn't tolerable",
          body: (
            <div className="space-y-5">
              <p>
                Older adults are more likely than younger ones to find that
                medication doesn't fully work, or that the side effects —
                dizziness, falls, confusion, stomach trouble — are worse than
                the depression. If you've had two adequate trials without
                relief, you have treatment-resistant depression, and adding a
                third medication has about a 14 percent chance of producing
                remission while adding to the side-effect burden.
              </p>
              <p>
                There's a different approach. Depression isn't only a chemistry
                problem. It's a circuitry problem: the region behind the
                forehead that regulates mood is underactive, and no pill
                retrains it directly. Treatments that work on the circuit itself
                don't go through your bloodstream, which at 70 is a significant
                advantage.
              </p>
            </div>
          ),
        },
        {
          heading: "SAINT for an older adult",
          body: (
            <div className="space-y-4">
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an FDA-cleared, accelerated form of transcranial magnetic
                stimulation developed at Stanford's Brain Stimulation Lab for
                adults whose depression hasn't responded adequately to
                medication. It uses a 45-minute functional MRI of your own brain
                to identify the exact target, then delivers ten 10-minute
                stimulation sessions a day, with 50-minute breaks, for five
                consecutive days.
              </p>
              <p>
                Here's how it works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation.</strong> Our clinical team reviews your
                  full medical and psychiatric history and confirms you're a
                  candidate. The initial consultation is a Zoom call; as steps
                  evolve, you'll also have the option to meet by Zoom web
                  conference or in person. The mandatory in-person steps include
                  fMRI mapping and the actual treatment.
                </li>
                <li>
                  <strong>fMRI brain scan.</strong> A roughly 45-minute
                  functional MRI maps how your individual brain networks are
                  wired.
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location for
                  stimulation.
                </li>
                <li>
                  <strong>Five-day stimulation series.</strong> Ten 10-minute
                  sessions per day, with 50-minute breaks in between, for five
                  consecutive days.
                </li>
              </ol>

              <SaintComparisonStatBand />

              <p>
                For someone at 70, the important points: it's not a drug, so it
                doesn't interact with anything you're taking. It requires no
                anesthesia, unlike ECT. It's five days, so it doesn't require
                weeks of transportation and daily appointments. In clinical
                trials of people who had failed medication, 79 percent reached
                remission, with an average time to remission of 2.6 days. Across
                more than 400 trial patients, 85 percent experienced symptom
                relief, with no serious adverse events. Side effects were mostly
                mild headache, fatigue, and scalp discomfort.
              </p>
              <p>
                It isn't appropriate for people with non-removable metal in or
                near the head or an implanted device such as a pacemaker, which
                is carefully screened at consultation. Your current medications
                continue.
              </p>
            </div>
          ),
        },
        {
          heading: "What the years ahead can hold",
          body: (
            <div className="space-y-5">
              <p>
                Seventy is not the end of your story. Many people have fifteen
                or twenty years ahead, and the difference between spending them
                depressed and spending them well is enormous, for you and for
                the people who love you. Grandchildren, friendships, the garden,
                the book, the trip. Those are available to a person who is well,
                and being well is a realistic goal.
              </p>
            </div>
          ),
        },
        {
          heading: "For adult children reading this",
          body: (
            <div className="space-y-5">
              <p>
                If your parent has been diagnosed with depression, or if you
                suspect it, don't let it be filed under aging. Help them get to
                the appointment. Help them give the treatment a real chance. And
                if medication hasn't worked or hasn't been tolerable, know that
                there are non-drug options built for exactly this situation.
                Family members are welcome at every step.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                At Neuro Wellness TMS Centers of America, a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>
                , the first appointment is an evaluation, not a commitment. We
                offer private outpatient care with a dedicated care team and
                weekend availability, and family members are welcome. We'll
                review your full medical and psychiatric picture and tell you
                honestly whether SAINT is right for you. Medicare and other
                coverage varies; our team can help you understand your options.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you or a loved one are in crisis or thinking about self-harm,
                call or text 988 (Suicide &amp; Crisis Lifeline) or go to your
                nearest emergency room. This article is for educational purposes
                and is not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
